import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Container from "@/components/marketing/Container";
import Seo from "@/components/Seo";
import { base44 } from "@/api/base44Client";
import PackagesHero from "@/components/marketing/service/packages/PackagesHero";
import PackagesFilterBar from "@/components/marketing/service/packages/PackagesFilterBar";
import PackagesGrid from "@/components/marketing/service/packages/PackagesGrid";
import PackageDetail from "@/components/marketing/service/packages/PackageDetail";
import HowPackagesWork from "@/components/marketing/service/packages/HowPackagesWork";
import PackagesBridge from "@/components/marketing/service/packages/PackagesBridge";
import PackagesCTA from "@/components/marketing/service/packages/PackagesCTA";
import { resolvePackagePrices, resolvePackageComponents } from "@/lib/packagePriceResolver";
import Reveal from "@/components/marketing/Reveal";

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    base44.entities.Packages.filter({ active: true }, "sort_order", 100)
      .then(async (data) => {
        if (!active) return;
        const resolved = await resolvePackagePrices(data || []);
        if (!active) return;
        setPackages(resolved);
        setLoading(false);
        // Phase 2: resolve component outlets for logo chips. Runs after first
        // paint (setPackages above) so cards render immediately, then a second
        // state update attaches `components` when this completes.
        const withComponents = await resolvePackageComponents(resolved);
        if (!active) return;
        setPackages(withComponents);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const availableRegions = useMemo(() => {
    const set = new Set();
    packages.forEach((p) => { if (p.region) set.add(p.region); });
    return Array.from(set);
  }, [packages]);

  const availableFocuses = useMemo(() => {
    const set = new Set();
    packages.forEach((p) => { if (p.category_tag) set.add(p.category_tag); });
    return Array.from(set);
  }, [packages]);

  const [searchParams, setSearchParams] = useSearchParams();
  const regionParam = searchParams.get("region") || "";
  const focusParam = searchParams.get("focus") || "";

  const selectedRegion = ["All", "UK", "US", "UAE", "GCC", "MENA", "Africa", "Global"].find((r) => r.toLowerCase() === regionParam.toLowerCase()) || "All";
  const selectedFocus = availableFocuses.find((f) => f.toLowerCase() === focusParam.toLowerCase()) || "All";

  const handleRegionChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.delete("region");
    else next.set("region", value.toLowerCase());
    setSearchParams(next, { replace: true });
  };

  const handleFocusChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.delete("focus");
    else next.set("focus", value.toLowerCase());
    setSearchParams(next, { replace: true });
  };

  const handleResetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const filteredPackages = useMemo(() => {
    return packages.filter((p) => {
      const regionMatch = selectedRegion === "All" || p.region === selectedRegion;
      const focusMatch = selectedFocus === "All" || (p.category_tag && p.category_tag.toLowerCase() === selectedFocus.toLowerCase());
      return regionMatch && focusMatch;
    });
  }, [packages, selectedRegion, selectedFocus]);

  const isFiltered = selectedRegion !== "All" || selectedFocus !== "All";

  const [introDone, setIntroDone] = useState(false);
  useEffect(() => {
    if (!loading && packages.length > 0 && !introDone) {
      const t = setTimeout(() => setIntroDone(true), 900);
      return () => clearTimeout(t);
    }
  }, [loading, packages.length, introDone]);

  return (
    <div className="bg-[#f7f7f9]">
      <Seo title="Packages | Digital24" />
      <PackagesHero />
      <PackagesFilterBar
        availableRegions={availableRegions}
        availableFocuses={availableFocuses}
        selectedRegion={selectedRegion}
        selectedFocus={selectedFocus}
        onRegionChange={handleRegionChange}
        onFocusChange={handleFocusChange}
      />
      <PackagesGrid
        packages={filteredPackages}
        loading={loading}
        isFiltered={isFiltered}
        onResetFilters={handleResetFilters}
        selectedRegion={selectedRegion}
      />
      {filteredPackages.length > 0 && (
        <section className="bg-[#f7f7f9] py-10 sm:py-12 lg:py-14">
          {introDone ? (
            <Reveal key={selectedRegion + "|" + selectedFocus} variant="ack" mode="mount">
              <Container className="space-y-8 lg:space-y-10">
                {filteredPackages.map((p) => (
                  <PackageDetail key={p.id || p.slug} pkg={p} />
                ))}
              </Container>
            </Reveal>
          ) : (
            <Container className="space-y-8 lg:space-y-10">
              {filteredPackages.map((p) => (
                <Reveal key={p.id || p.slug}>
                  <PackageDetail pkg={p} />
                </Reveal>
              ))}
            </Container>
          )}
        </section>
      )}
      <HowPackagesWork />
      <PackagesBridge />
      <PackagesCTA />
    </div>
  );
}